import React, { useEffect, useMemo, useRef, useState } from "react";
import AdBanner from "./AdBanner";
import { useSubscription } from "../../contexts/SubscriptionContext";

function normalizeApiBase(raw) {
  let s = String(raw || "").trim();
  if (!s) return s;

  s = s.replace(/\/+$/, "");

  s = s.replace(/\/api$/i, "");

  return s;
}

function resolveAdsDebugFlag() {
  try {
    if (typeof window === "undefined") return false;

    const qs = new URLSearchParams(window.location.search || "");
    if (qs.get("adsDebug") === "1") return true;

    const ls = window.localStorage?.getItem("adsDebug");
    if (ls === "1" || ls === "true") return true;

    return false;
  } catch {
    return false;
  }
}

const RAW_API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const API_BASE = normalizeApiBase(RAW_API_BASE);
const ADS_ENABLED = String(process.env.REACT_APP_ADS_ENABLED || "").toLowerCase() === "true";

async function fetchJson(url) {
  const res = await fetch(url, { headers: { "Content-Type": "application/json" } });
  const ct = res.headers.get("content-type") || "";
  const text = ct.includes("application/json") ? null : await res.text().catch(() => "");
  if (!res.ok) throw new Error(`HTTP ${res.status}${text ? `: ${text.slice(0, 120)}` : ""}`);
  if (ct.includes("application/json")) return res.json();
  throw new Error(`Expected JSON, got: ${String(text || "").slice(0, 120)}`);
}

async function postJson(url, body) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
      keepalive: true,
    });
  } catch (e) {
  }
}

// The one place entitlements.externalAdsEnabled is interpreted for ads —
// exported so it's unit-testable without rendering, and so every call site
// (Feed/Search/Home/Messenger/ListingDetails, all via <AdSlot>) shares
// this exact rule instead of re-deriving it.
export function computeCanSeeAds(entitlements) {
  return entitlements?.externalAdsEnabled !== false;
}

function isOnScreen(el, minVisiblePx = 40) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;

  const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
  return visible >= minVisiblePx;
}

// MVP impression dedup — not fraud detection, just "don't let an ordinary
// SPA remount/scroll-back-and-forth recount the same campaign's impression
// within one browser session." Keyed per campaignId (not globally) so
// different campaigns never block each other; a fresh browser session
// (sessionStorage cleared) can always count again.
const IMPRESSION_SESSION_KEY = "cvanto:ads:impressed";

function readImpressedSet() {
  try {
    const raw = window.sessionStorage?.getItem(IMPRESSION_SESSION_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

// Exported for unit tests — pure, no component rendering needed.
export function hasCountedImpression(campaignId) {
  if (!campaignId) return false;
  return readImpressedSet().includes(campaignId);
}

// Exported for unit tests — pure, no component rendering needed.
export function markImpressionCounted(campaignId) {
  if (!campaignId) return;
  try {
  const set = readImpressedSet();
    if (set.includes(campaignId)) return;
    // a session realistically sees a handful of distinct campaigns, not
    // thousands — cap defensively so this never grows unbounded.
    const next = [...set, campaignId].slice(-200);
    window.sessionStorage?.setItem(IMPRESSION_SESSION_KEY, JSON.stringify(next));
  } catch {}
}

export default function AdSlot({
  placement = "feed_inline_1",
  threshold = 0.25,
  debug,
}) {
  const debugOn = useMemo(() => {
    if (typeof debug === "boolean") return debug;
    return resolveAdsDebugFlag();
  }, [debug]);

  // Centralized eligibility check — the ONLY place in the app that reads
  // entitlements.externalAdsEnabled for ads. Every placement (Feed,
  // Search, Listing Details, Homepage Banner, Messenger) renders <AdSlot>
  // unconditionally and lets this decide whether to actually fetch/show
  // anything, instead of each caller re-implementing its own plan check.
  const { entitlements } = useSubscription();
  const canSeeAds = computeCanSeeAds(entitlements);

  const [campaign, setCampaign] = useState(null);
  const sentImpressionRef = useRef(false);
  const rootRef = useRef(null);
  const mountIdRef = useRef(Math.random().toString(16).slice(2, 8));

  if (debugOn) {
    console.log("[ads][slot] render", {
      mountId: mountIdRef.current,
      placement,
      hasCampaign: !!campaign,
      campaignId: campaign?.id || null,
      api: API_BASE,
    });
  }

  // 1) serve
  useEffect(() => {
  let alive = true;

  if (!ADS_ENABLED || !canSeeAds) {
    if (debugOn) {
      console.log("[ads][slot] ads disabled or ineligible, skip serve", {
        mountId: mountIdRef.current,
        placement,
        adsEnabled: ADS_ENABLED,
        canSeeAds,
      });
    }
    setCampaign(null);
    return () => {
      alive = false;
    };
  }

  if (debugOn) console.log("[ads][slot] MOUNT", { mountId: mountIdRef.current, placement, api: API_BASE });

  (async () => {
    const url = `${API_BASE}/api/ads/serve?placement=${encodeURIComponent(placement)}`;
      try {
        if (debugOn) console.log("[ads][slot] serve ->", { url, mountId: mountIdRef.current, placement });

        const data = await fetchJson(url);

        if (!alive) return;

        const next = data && data.campaign ? data.campaign : null;
        setCampaign(next);

        if (debugOn) {
          console.log("[ads][slot] serve OK", {
            mountId: mountIdRef.current,
            placement,
            got: !!next,
            id: next?.id || null,
            startAt: next?.startAt || null,
            endAt: next?.endAt || null,
          });
        }
      } catch (e) {
        if (!alive) return;

        if (debugOn) {
          console.warn("[ads][slot] serve ERROR", {
            mountId: mountIdRef.current,
            placement,
            api: API_BASE,
            err: String(e?.message || e),
          });
        }

        setCampaign(null);
      }
    })();

    return () => {
      alive = false;
      if (debugOn) console.log("[ads][slot] UNMOUNT", { mountId: mountIdRef.current, placement });
    };
  }, [placement, debugOn, canSeeAds]);

  // 2) impression once (observer + fallback)
  useEffect(() => {
    if (!campaign || !campaign.id) {
      if (debugOn) console.log("[ads][slot] impression: no campaign", { mountId: mountIdRef.current, placement });
      return;
    }
    if (!rootRef.current) {
      if (debugOn) console.warn("[ads][slot] impression: rootRef missing", { mountId: mountIdRef.current });
      return;
    }

    sentImpressionRef.current = false;

    const el = rootRef.current;

    if (debugOn) {
      const rect = el.getBoundingClientRect();
      console.log("[ads][slot] el metrics", {
        mountId: mountIdRef.current,
        h: el.offsetHeight,
        w: el.offsetWidth,
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
        vh: window.innerHeight,
        threshold,
        campaignId: campaign.id,
      });
    }

    const fire = (reason) => {
      if (sentImpressionRef.current) return;
      sentImpressionRef.current = true;

      if (hasCountedImpression(campaign.id)) {
        if (debugOn) {
          console.log("[ads][slot] IMPRESSION SKIP (already counted this session)", {
            mountId: mountIdRef.current,
            placement,
            campaignId: campaign.id,
            reason,
          });
        }
        return;
      }

      if (debugOn) {
        console.log("[ads][slot] IMPRESSION FIRE", {
          mountId: mountIdRef.current,
          placement,
          campaignId: campaign.id,
          reason,
        });
      }

      markImpressionCounted(campaign.id);
      postJson(`${API_BASE}/api/ads/impression`, { campaignId: campaign.id });
    };

    if (debugOn) {
      console.log("[ads][slot] observer attach", {
        mountId: mountIdRef.current,
        placement,
        threshold,
        campaignId: campaign.id,
      });
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        if (!entry) return;

        const r = typeof entry.intersectionRatio === "number" ? entry.intersectionRatio : 0;

        if (debugOn) {
          console.log("[ads][slot] intersect", {
            mountId: mountIdRef.current,
            isIntersecting: entry.isIntersecting,
            ratio: r,
            threshold,
            sent: sentImpressionRef.current,
            campaignId: campaign.id,
          });
        }

        if (entry.isIntersecting && r >= threshold) {
          fire("observer");
        }
      },
      {
        threshold: [0, 0.05, 0.1, threshold, 0.25, 0.5, 0.75, 1],
        root: null,
        rootMargin: "0px",
      }
    );

    obs.observe(el);

    const t = setTimeout(() => {
      if (sentImpressionRef.current) return;
      if (isOnScreen(el, 40)) fire("fallback-rect");
      else if (debugOn) console.log("[ads][slot] fallback-rect: not on screen yet", { mountId: mountIdRef.current });
    }, 400);

    const onScroll = () => {
      if (sentImpressionRef.current) return;
      if (isOnScreen(el, 40)) fire("scroll-rect");
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);

      try {
        obs.unobserve(el);
      } catch {}
      try {
        obs.disconnect();
      } catch {}

      if (debugOn) {
        console.log("[ads][slot] observer detach", {
          mountId: mountIdRef.current,
          placement,
          campaignId: campaign.id,
        });
      }
    };
  }, [campaign, threshold, placement, debugOn]);

  if (!ADS_ENABLED || !canSeeAds) return null;
  if (!campaign && !debugOn) return null;

  return (
    <div
      ref={rootRef}
      data-ad-slot="1"
      data-placement={placement}
      style={
        debugOn
          ? {
              outline: "1px dashed rgba(255,0,0,.35)",
              borderRadius: 12,
              padding: 6,
              margin: "10px 0",
              minHeight: 80,
            }
          : undefined
      }
    >
      {campaign ? (
        <AdBanner campaign={campaign} apiBase={API_BASE} />
      ) : debugOn ? (
        <div style={{ padding: 10, fontSize: 13, opacity: 0.7 }}>
          AdSlot: немає campaign (placement: <b>{placement}</b>)
        </div>
      ) : null}
    </div>
  );
}